/**
 * The single rule for "a frame can be grabbed right now".
 *
 * Three entry points reach the capture flow — the C shortcut, the transport's
 * Capture button and the chat's Capture frame tool. The buttons merely
 * disable when capture is unavailable, so the key press used to sail straight
 * past them and destroy an open markup. They all read this.
 *
 * Reply mode is the chat's concern: its tools row hides while replying, and a
 * moment staged mid-reply cancels the reply there.
 */

export interface CaptureReadiness {
  /** The whole anchored-notes session is live. */
  enabled: boolean;
  /** The pixels are readable at all (CORS, and an untainted canvas). */
  canCapture: boolean;
  /** Metadata loaded and the first frame is decodable. */
  ready: boolean;
  composerDisabled: boolean;
  /** A frame is already grabbed and being marked up. */
  annotating: boolean;
}

export function isCaptureAvailable({ enabled, canCapture, ready, composerDisabled, annotating }: CaptureReadiness): boolean {
  if (!enabled || !canCapture || !ready) return false;
  if (composerDisabled) return false;
  return !annotating;
}

/** The video plays, but where it is stored doesn't allow its frames to be read (no CORS). */
export const CAPTURE_UNAVAILABLE_TITLE = "Frames can't be captured from this video. You can still add a timestamp.";

/** What the "frames can't be read" verdict needs. */
export type CaptureSupport = Pick<CaptureReadiness, 'canCapture' | 'ready'>;

/** Loaded, yet its pixels can't be read: capture will never work for this video, so every capture button says why. */
export function isCaptureUnsupported({ ready, canCapture }: CaptureSupport): boolean {
  return ready && !canCapture;
}
